import { Trans, t } from '@lingui/macro'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { Box, Button, IconButton } from '@mui/material'
import { useUserPermissions } from '@om1/platform-authentication'
import { LightTooltip } from '@om1/platform-components/Tooltip'
import { Routes, toPath } from '@om1/platform-routing'
import { PlatformPermissions, RoutedFrameworkComponentProps } from '@om1/platform-utils'
import { FunctionComponent, useEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { PatientCount } from '../../components/edit/base/PatientCount'
import { createCohortReports } from '../../components/reports/CohortReports'
import { CohortStickyHeader } from '../../components/shared/CohortStickyHeader'
import { CohortRoutes } from '../../routes'
import { CohortListItem, ReportAnalyticsType, cohortEditActions } from '../../state'
import { CohortDashboardCohortCard } from '../CohortDashboardPage/CohortDashboardCohortCard'
const CohortReports = createCohortReports<{}>()

export type ReportsPageComponentProps = RoutedFrameworkComponentProps<
    {},
    { cohortId: string | undefined; reportType: string | undefined },
    { cohort: CohortListItem; cohortLoading: boolean },
    typeof cohortEditActions,
    {}
>

export const ReportsPageComponent: FunctionComponent<ReportsPageComponentProps> = ({ actions, routing, state: { cohort, cohortLoading } }) => {
    const history = useHistory()
    const { permissions } = useUserPermissions()
    useEffect(() => {
        actions.cohortGet({ query: {}, path: { cohortId: routing.pathParams.cohortId } })
    }, [actions, routing.pathParams.cohortId])

    useEffect(() => {
        if (cohort && !routing.pathParams.reportType) {
            history.push(`/cohort-builder/reports/${routing.pathParams.cohortId}/${ReportAnalyticsType.DEMOGRAPHICS}`)
        }
    }, [history, routing.pathParams.cohortId, routing.pathParams.reportType, cohort])

    const breadcrumbs = (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
                startIcon={<ArrowBackIcon />}
                component={Link}
                to={`/${CohortRoutes.COHORTBUILD}/dashboard/${cohort.isSystem ? CohortRoutes.DATASETS : CohortRoutes.CUSTOM}/${cohort.id}`}
                sx={{
                    fontWeight: '600',
                    fontSize: '16px',
                    color: '#707171',
                    textTransform: 'none',
                    '&:hover': {
                        backgroundColor: 'transparent',
                        opacity: 0.8
                    },
                    paddingBottom: '1px',
                    paddingTop: '1px'
                }}
            >
                {t`Back to Dashboard`}
            </Button>
        </Box>
    )

    let cardActions: JSX.Element[] = []

    cardActions.push(<PatientCount key={'count'} cohort={cohort} loading={cohortLoading} />)

    if (!cohort.isSystem && permissions?.includes(PlatformPermissions.UPDATE_COHORT)) {
        cardActions.push(
            <LightTooltip key={'editTooltip'} title={<Trans>Edit Cohort</Trans>} placement='top'>
                <IconButton
                    key='edit'
                    color='primary'
                    component={Link}
                    to={toPath(Routes.COHORTBUILD) + `/custom/${cohort.id}/edit`}
                    aria-label={t`Edit`}
                    data-testid='edit-cohort-button'
                    sx={{ backgroundColor: '#D6E4F2', color: '#012D72', borderRadius: '2px', margin: '5px', marginLeft: '20px' }}
                >
                    <EditOutlinedIcon />
                </IconButton>
            </LightTooltip>
        )
    }

    return (
        <>
            <Box display='flex' flexDirection='column' gap={1}>
                {breadcrumbs}
                <CohortStickyHeader>
                    <Box
                        sx={{
                            paddingTop: '0px',
                            paddingBottom: '0px'
                        }}
                    >
                        <CohortDashboardCohortCard cohort={cohort} actions={cardActions} excludeTitleRowFromCard={true} />
                    </Box>
                </CohortStickyHeader>

                {routing.pathParams.reportType && cohort && (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <CohortReports cohort={cohort} />
                    </Box>
                )}
            </Box>
        </>
    )
}
